/**
 * P17A-06 · The anonymous-visitor id, minted in the browser.
 *
 * P9-01 minted `vx_anon` in `src/middleware.ts`, on the first response of every
 * visit. That is one `Set-Cookie` on the storefront HTML — and a response that
 * sets a cookie is not storable by a shared cache, so it made every first view
 * of every page `cf-cache-status: DYNAMIC` no matter what the `Cache-Control`
 * said. It was also unconditional: a crawler that will never emit an event, and
 * a visitor who declined analytics, both got one.
 *
 * The cookie was already `httpOnly: false` precisely so the browser could read
 * it, so moving the mint to the browser costs nothing and is called from the
 * three surfaces that actually need the id to exist:
 *
 *  - `analytics/client.ts` — the ingest procedure reads the cookie off the POST
 *    (`readAnonId`) and drops the batch without one;
 *  - `cart/use-cart.tsx` — the checkout stamps the analytics session onto the
 *    `CheckoutSession` while the cookie is still readable (the order webhook
 *    has no cookies), so a shopper who adds to cart must already have an id;
 *  - `wishlist/use-wishlist.ts` — a guest save is the same kind of first
 *    engagement, and `WISHLIST_ADD` is a captured event.
 *
 * Both server readers already tolerate absence and did before this change
 * (`ingest.ts` returns `{ accepted: 0 }`, `checkout-service.ts` skips the
 * stamp), so nothing has to defend against a missing cookie that wasn't
 * already defending against it.
 */
import {
  readClientCookie,
  writeClientCookie,
} from "~/lib/cookies/client-cookie";
import { VX_ANON_COOKIE } from "./events";

/** A year, unchanged from the middleware mint it replaces. */
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

function newId(): string {
  // `crypto.randomUUID` is unavailable outside a secure context, which is
  // exactly where this can still be asked to run (a plain-HTTP preview host).
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * The visitor's `vx_anon` id, minting and storing one if there isn't one yet.
 * Returns null on the server, and when the browser refused the cookie.
 *
 * Attributes come from `writeClientCookie` — one place for the browser's
 * cookies, the way `serializeCookie()` is one place for the server's. In
 * particular `Secure` is decided there, on `isSecureContext` rather than on
 * `NODE_ENV`; that file argues why the two sides have to differ.
 */
export function ensureAnonId(): string | null {
  if (typeof document === "undefined") return null;
  const existing = readClientCookie(VX_ANON_COOKIE);
  if (existing) return existing;

  writeClientCookie(VX_ANON_COOKIE, newId(), { maxAge: MAX_AGE_SECONDS });

  // Read back rather than returning the minted value: if the browser refused
  // the write (third-party context, storage blocked), the caller should learn
  // that no id exists rather than send one the next request will not carry.
  return readClientCookie(VX_ANON_COOKIE);
}
